// Het validatiedocument voor de accountmanager (VT-1) — /validatie/<propositie>.
//
// Een losse pagina, zonder de tabbladen van het dashboard: de AM komt hier voor
// één ding, en de schets in "Een sector erbij" (editie 2, vraag 3) is één
// vraag per blok met drie knoppen per regel. Wat hier bewust zo is:
//
//  · Elke klik is meteen één POST. Er is geen "opslaan"-knop, dus ook niets om
//    te vergeten: stoppen en later verder gaan werkt vanzelf. Het scherm zet
//    de keuze direct neer en draait hem terug als de server nee zegt.
//  · "Anders" opent een tekstveld dat bewaart zodra je het verlaat. Het oordeel
//    zelf is dan al bewaard; de tekst komt eroverheen.
//  · De SERVER bepaalt wat de AM ziet (vaste regels verdwijnen) en telt de
//    voortgang; het scherm rekent niets na.
//  · Licht en donker via eigen variabelen onder `.vld` (styles.css): de rest van
//    het dashboard kent nog geen donkere stand, deze pagina wel.

import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "../lib/api";
import type {
  ValidatieBlok,
  ValidatieItem,
  ValidatieOordeel,
  ValidatiePagina,
  ValidatieVoortgang,
} from "../contracts";

type Stand =
  | { stand: "laden" }
  | { stand: "fout"; reden: string }
  | { stand: "klaar"; pagina: ValidatiePagina };

/** De reden in woorden. Bij 400/403/404/409 stuurt de dienst een eigen
 *  Nederlandse zin ("dit document is voor de AM van deze school") die meer zegt
 *  dan het algemene "geen toegang — opnieuw inloggen" van ApiError.reason; een
 *  AM van een andere school opnieuw laten inloggen helpt hem niet. */
function reden(err: unknown, anders: string): string {
  if (!(err instanceof ApiError)) return anders;
  return [400, 403, 404, 409].includes(err.status) && err.message ? err.message : err.reason;
}

/** Het eerste blok met nog iets open — zo gaat "later verder" echt verder. */
function startBlok(p: ValidatiePagina): number {
  const i = p.blokken.findIndex((b) => b.voortgang.beantwoord < b.voortgang.totaal);
  return i === -1 ? 0 : i;
}

/** Eén item in de pagina vervangen, zonder de rest aan te raken. */
function metItem(p: ValidatiePagina, id: string, nieuw: Partial<ValidatieItem>): ValidatiePagina {
  return {
    ...p,
    blokken: p.blokken.map((b) => ({
      ...b,
      groepen: b.groepen.map((g) => ({
        ...g,
        items: g.items.map((i) => (i.id === id ? { ...i, ...nieuw } : i)),
      })),
    })),
  };
}

/** De balk per blok: de antwoorden die er nu staan, zodat hij direct na een
 *  klik meeloopt. Het totaal bovenaan komt van de server (na elke klik). */
function blokVoortgang(b: ValidatieBlok): ValidatieVoortgang {
  const tellend = b.groepen.flatMap((g) => g.items).filter((i) => !i.vrij);
  const klaar = tellend.filter((i) => i.antwoord).length;
  return { beantwoord: klaar, totaal: tellend.length, tekst: `${klaar} van ${tellend.length}` };
}

export function ValidatiePagina({ propositie }: { propositie: string }) {
  const [stand, setStand] = useState<Stand>({ stand: "laden" });
  const [blokNr, setBlokNr] = useState(0);
  const kopRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let levend = true;
    api
      .validatie(propositie)
      .then((p) => {
        if (!levend) return;
        setStand({ stand: "klaar", pagina: p });
        setBlokNr(startBlok(p));
      })
      .catch((err: unknown) => {
        if (!levend) return;
        setStand({
          stand: "fout",
          reden: reden(err, "Het document kon niet worden geladen."),
        });
      });
    return () => {
      levend = false;
    };
  }, [propositie]);

  if (stand.stand === "laden") {
    return (
      <div className="vld">
        <main className="vld-wrap">
          <p className="vld-hint">Het document wordt geladen…</p>
        </main>
      </div>
    );
  }
  if (stand.stand === "fout") {
    return (
      <div className="vld">
        <main className="vld-wrap">
          <h1 className="vld-titel">Dit document is niet te openen</h1>
          <p className="vld-fout" role="alert">
            {stand.reden}
          </p>
          <a className="vld-link" href="/">
            Naar het dashboard
          </a>
        </main>
      </div>
    );
  }

  const pagina = stand.pagina;
  // Een document zonder blokken laat de server niet door (controleer()); dit
  // vangt alleen een blokNr dat niet (meer) bestaat.
  const blok = pagina.blokken[blokNr] ?? pagina.blokken[0];
  if (!blok) return null;
  const naarBlok = (nr: number) => {
    setBlokNr(nr);
    window.scrollTo({ top: 0 });
    // De kop krijgt de focus, zodat een schermlezer het nieuwe blok aankondigt.
    requestAnimationFrame(() => kopRef.current?.focus());
  };
  const klaar = pagina.voortgang.beantwoord >= pagina.voortgang.totaal;

  return (
    <div className="vld">
      <main className="vld-wrap">
        <header className="vld-bar">
          <a className="vld-link" href="/">
            ← dashboard
          </a>
          <span aria-live="polite">{pagina.voortgang.tekst}</span>
        </header>

        {/* Release 06-10: een document uit de bril heeft twee blokken, een
            handgeschreven drie; de balk volgt het aantal. */}
        <nav
          className="vld-stappen"
          aria-label="Blokken"
          style={{ gridTemplateColumns: `repeat(${pagina.blokken.length}, 1fr)` }}
        >
          {pagina.blokken.map((b, nr) => {
            const v = blokVoortgang(b);
            return (
              <button
                key={b.sleutel}
                type="button"
                className={"vld-stap" + (nr === blokNr ? " actief" : "")}
                aria-current={nr === blokNr ? "step" : undefined}
                onClick={() => naarBlok(nr)}
              >
                <span className="vld-stap-balk">
                  <span
                    style={{ width: v.totaal ? `${(100 * v.beantwoord) / v.totaal}%` : "0%" }}
                  />
                </span>
                <span className="vld-stap-naam">
                  {nr + 1} · {b.titel}
                </span>
                <span className="vld-stap-tel">{v.tekst}</span>
              </button>
            );
          })}
        </nav>

        {blokNr === 0 && (
          <section className="vld-intro">
            <h1 className="vld-titel">{pagina.titel}</h1>
            {pagina.intro && <p>{pagina.intro}</p>}
          </section>
        )}

        {pagina.isBeheerder && (
          <p className="vld-beheer">
            Je kijkt als beheerder. Zet op <b>vast</b> wat al vaststaat: de accountmanager ziet
            die regels dan niet meer. Je eigen antwoorden tellen mee in het overzicht in Beheer.
          </p>
        )}

        <section aria-labelledby="vld-vraag">
          <h2 id="vld-vraag" className="vld-vraag" tabIndex={-1} ref={kopRef}>
            {blok.vraag}
          </h2>
          {blok.hint && <p className="vld-hint">{blok.hint}</p>}

          {blok.groepen.map((g) => (
            <div key={g.sleutel} className="vld-groep">
              {g.titel && <h3 className="vld-groep-titel">{g.titel}</h3>}
              {g.items.map((item) => (
                <Regel
                  key={item.id}
                  item={item}
                  knoppen={blok.knoppen.map((k) => k.sleutel)}
                  labels={Object.fromEntries(blok.knoppen.map((k) => [k.sleutel, k.label]))}
                  propositie={propositie}
                  isBeheerder={pagina.isBeheerder}
                  // Functioneel bijwerken: het antwoord van de server komt ná
                  // een await terug, en tussendoor kan een andere regel zijn
                  // aangeklikt — een oude `pagina` zou die klik wissen.
                  onWijzig={(nieuw) => setStand((s) =>
                    s.stand === "klaar"
                      ? { ...s, pagina: metItem(s.pagina, item.id, nieuw) }
                      : s,
                  )}
                  onVoortgang={(v) => setStand((s) =>
                    s.stand === "klaar" ? { ...s, pagina: { ...s.pagina, voortgang: v } } : s,
                  )}
                />
              ))}
            </div>
          ))}
        </section>

        <footer className="vld-voet">
          {blokNr > 0 && (
            <button type="button" className="vld-knop" onClick={() => naarBlok(blokNr - 1)}>
              ← Vorig blok
            </button>
          )}
          {blokNr < pagina.blokken.length - 1 ? (
            <button
              type="button"
              className="vld-knop vld-knop-hoofd"
              onClick={() => naarBlok(blokNr + 1)}
            >
              Volgend blok →
            </button>
          ) : (
            <p className="vld-hint" role="status">
              {klaar
                ? "Klaar. Dank je wel: alles is bewaard."
                : "Alles wat je koos is bewaard. Je kunt dit venster sluiten en later verdergaan."}
            </p>
          )}
        </footer>
      </main>
    </div>
  );
}

function Regel({
  item,
  knoppen,
  labels,
  propositie,
  isBeheerder,
  onWijzig,
  onVoortgang,
}: {
  item: ValidatieItem;
  knoppen: ValidatieOordeel[];
  labels: Record<string, string>;
  propositie: string;
  isBeheerder: boolean;
  onWijzig: (nieuw: Partial<ValidatieItem>) => void;
  onVoortgang: (v: ValidatieVoortgang) => void;
}) {
  const [tekst, setTekst] = useState(item.antwoord?.tekst ?? "");
  const [melding, setMelding] = useState<{ fout: boolean; tekst: string } | null>(null);
  const oordeel = item.antwoord?.oordeel ?? null;
  const toonTekst = item.vrij || oordeel === "anders" || oordeel === "nee";

  const bewaar = async (nieuw: ValidatieOordeel, t: string | null) => {
    const vorig = item.antwoord;
    onWijzig({ antwoord: { oordeel: nieuw, tekst: t, op: vorig?.op ?? null } });
    setMelding(null);
    try {
      const res = await api.validatieAntwoord(propositie, {
        item: item.id,
        oordeel: nieuw,
        tekst: t,
      });
      onWijzig({ antwoord: res.antwoord });
      onVoortgang(res.voortgang);
      setMelding({ fout: false, tekst: "bewaard" });
    } catch (err) {
      onWijzig({ antwoord: vorig });
      setMelding({
        fout: true,
        tekst: reden(err, "Niet bewaard — probeer het opnieuw."),
      });
    }
  };

  const kies = (k: ValidatieOordeel) => {
    // Een tekst bij "anders" of "nee" hoort bij DIT oordeel; wie daarna op
    // "klopt" klikt, bedoelt die tekst niet meer.
    const t = k === "anders" || k === "nee" ? tekst.trim() || null : null;
    if (k !== "anders" && k !== "nee") setTekst("");
    void bewaar(k, t);
  };

  const tekstBewaren = () => {
    const t = tekst.trim();
    if (item.vrij) {
      if (t && t !== (item.antwoord?.tekst ?? "")) void bewaar("anders", t);
      return;
    }
    if (oordeel && t !== (item.antwoord?.tekst ?? "")) void bewaar(oordeel, t || null);
  };

  const zetVast = async (vast: boolean) => {
    // Direct omzetten, net als de oordeelknoppen: een vinkje dat pas na de
    // server omgaat, voelt als een klik die niets deed (testkaart stap 7).
    setMelding(null);
    onWijzig({ vast });
    try {
      await api.validatieVast(propositie, { item: item.id, vast });
    } catch (err) {
      onWijzig({ vast: !vast });
      setMelding({ fout: true, tekst: reden(err, "Niet gelukt.") });
    }
  };

  const klasse =
    "vld-regel" + (oordeel === "weg" ? " weg" : "") + (item.vast ? " vast" : "");
  const tekstId = `vld-tekst-${item.id}`;

  return (
    <div className={klasse} data-item={item.id}>
      <div className="vld-regel-tekst">
        <p className="vld-regel-kop">
          {item.url ? (
            <a href={item.url} target="_blank" rel="noreferrer">
              {item.tekst}
            </a>
          ) : (
            item.tekst
          )}
        </p>
        {item.uitleg && <p className="vld-regel-uitleg">{item.uitleg}</p>}
        {(item.bron || item.datum) && !item.lijst && (
          <p className="vld-regel-meta">
            {[item.bron, item.datum].filter(Boolean).join(" · ")}
          </p>
        )}
        {item.lijst && (
          <ul className="vld-lijst">
            {item.lijst.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        )}
      </div>

      {!item.vrij && (
        <div className="vld-knoppen" role="group" aria-label={`Oordeel over: ${item.tekst}`}>
          {knoppen.map((k) => (
            <button
              key={k}
              type="button"
              className={"vld-oordeel o-" + k + (oordeel === k ? " aan" : "")}
              aria-pressed={oordeel === k}
              onClick={() => kies(k)}
            >
              {labels[k] ?? k}
            </button>
          ))}
        </div>
      )}

      {toonTekst && (
        <div className="vld-invoer">
          <label htmlFor={tekstId} className="vld-invoer-label">
            {item.vrij
              ? "Naam of adres"
              : oordeel === "nee"
                ? "Waarom niet? (mag leeg)"
                : "Hoe zou jij het zeggen?"}
          </label>
          <textarea
            id={tekstId}
            rows={2}
            maxLength={2000}
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
            onBlur={tekstBewaren}
          />
          {item.vrij && (
            <button type="button" className="vld-knop" onClick={tekstBewaren}>
              Bewaar
            </button>
          )}
        </div>
      )}

      <div className="vld-regel-voet">
        {isBeheerder && (
          <label className="vld-vast">
            <input
              type="checkbox"
              checked={item.vast}
              onChange={(e) => void zetVast(e.target.checked)}
            />
            vast
          </label>
        )}
        {melding && (
          <span className={melding.fout ? "vld-fout" : "vld-bewaard"} role="status">
            {melding.tekst}
          </span>
        )}
      </div>
    </div>
  );
}
